// Third party imports
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { RouteReuseStrategy } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalModule,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalRedirectComponent,
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';

// LOCAL
// components
import { AppComponent } from './app.component';
// services
import { DarkThemeService } from 'src/app/core/services/dark-theme/dark-theme.service';
// modules
import { AppRoutingModule } from './app-routing.module';
import { CoreModule } from './core/core.module';
import { LoadingModule } from './core/components/loading/loading.module';
import { LoginModule } from './core/components/login/login.module';
import { PageNotFoundModule } from './core/components/page-not-found/page-not-found.module';
import { ProfileModule } from './core/components/profile/profile.module';
// environment
import { environment } from 'src/environments/environment';
import { SideBarComponent } from './shared/side-bar/side-bar.component';
import { TradeContractRouteReuseStrategy } from './core/route-reuse-strategy';

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msal.clientId,
      authority: environment.msal.authority,
      redirectUri: environment.msal.redirectUri,
      postLogoutRedirectUri: '/',
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      allowNativeBroker: false, // Disables WAM Broker
      loggerOptions: {
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  // the access tokens for the below resources will be obtained by MSAL automatically
  protectedResourceMap.set('https://graph.microsoft.com/v1.0/me', ['user.read']);
  protectedResourceMap.set(environment.mainApi.rootUrl, environment.mainApi.scopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: ['user.read'],
    },
    loginFailedRoute: '/login-failed',
  };
}

@NgModule({
  bootstrap: [AppComponent, MsalRedirectComponent],
  declarations: [
    AppComponent
  ],
  imports: [
    SideBarComponent,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule,
    MsalModule,
    // LOCAL
    ProfileModule,
    LoginModule,
    PageNotFoundModule,
    LoadingModule
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    DarkThemeService,
    provideHttpClient(withInterceptorsFromDi()),
    { provide: RouteReuseStrategy, useClass: TradeContractRouteReuseStrategy }
  ]
})
export class AppModule { }


